/**
 * useKeyboardInset — publishes the on-screen keyboard's height as a custom
 * property on the chat pane (2026-09-14).
 *
 * See keyboardInset.ts for why this exists and why the shape is "make the
 * keyboard an ordinary height change" rather than teaching the scroll
 * controller about keyboards. This file is only the plumbing: listen, measure,
 * write, clean up.
 */
import { useEffect, useRef, type RefObject } from 'react';
import {
  KEYBOARD_INSET_VAR,
  keyboardInsetPx,
  insetChangeIsMeaningful,
} from './keyboardInset';

export interface UseKeyboardInsetArgs {
  /**
   * Fullscreen-only. In floating and docked modes the pane does not reach the
   * bottom of the screen, the page behind it is not scroll-locked, and the
   * browser's own focused-input handling is adequate.
   */
  enabled: boolean;
  /** The chat pane — the element whose `bottom` consumes the property. */
  targetRef: RefObject<HTMLElement | null>;
}

export function useKeyboardInset({ enabled, targetRef }: UseKeyboardInsetArgs): void {
  const lastWrittenRef = useRef(0);
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    const el = targetRef.current;
    if (!el) return undefined;

    const clear = (): void => {
      el.style.removeProperty(KEYBOARD_INSET_VAR);
      lastWrittenRef.current = 0;
    };

    // Not enabled, or a browser with no visual viewport (every desktop
    // browser predating it, and jsdom): leave the property unset, which
    // resolves to the 0px fallback and is byte-for-byte today's behaviour.
    const vv = typeof window !== 'undefined' ? window.visualViewport : undefined;
    if (!enabled || !vv) {
      clear();
      return undefined;
    }

    const measure = (): void => {
      frameRef.current = null;
      const next = keyboardInsetPx({
        layoutViewportHeight: document.documentElement?.clientHeight ?? 0,
        visualViewportHeight: vv.height,
        visualViewportOffsetTop: vv.offsetTop,
      });
      if (!insetChangeIsMeaningful(lastWrittenRef.current, next)) return;
      lastWrittenRef.current = next;
      if (next > 0) el.style.setProperty(KEYBOARD_INSET_VAR, `${Math.round(next)}px`);
      else el.style.removeProperty(KEYBOARD_INSET_VAR);
    };

    // Coalesced to one write per frame: iOS fires resize and scroll together
    // as the keyboard animates in, and each write changes layout.
    const schedule = (): void => {
      if (frameRef.current !== null) return;
      frameRef.current = typeof window.requestAnimationFrame === 'function'
        ? window.requestAnimationFrame(measure)
        : window.setTimeout(measure, 0) as unknown as number;
    };

    measure();
    // `scroll` as well as `resize`: iOS pans the visual viewport under a
    // focused input without changing its height, and the pane has to follow.
    vv.addEventListener('resize', schedule);
    vv.addEventListener('scroll', schedule);
    return () => {
      vv.removeEventListener('resize', schedule);
      vv.removeEventListener('scroll', schedule);
      if (frameRef.current !== null) {
        if (typeof window.cancelAnimationFrame === 'function') {
          window.cancelAnimationFrame(frameRef.current);
        } else {
          window.clearTimeout(frameRef.current);
        }
        frameRef.current = null;
      }
      clear();
    };
  }, [enabled, targetRef]);
}
